import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import PulsantiCondivisionePDF from './PulsantiCondivisionePDF';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils';

// ─── Stili PDF Programma Lavorativo ──────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page:    { padding: 30, fontSize: 11, fontFamily: 'Helvetica' },
  header:  { fontSize: 20, marginBottom: 20, textAlign: 'center', color: '#2c3e50', fontWeight: 'bold' },
  section: { marginBottom: 15, padding: 10, backgroundColor: '#f8f9fa', borderRadius: 5 },
  label:   { fontSize: 10, color: '#7f8c8d', marginBottom: 3 },
  value:   { fontSize: 12, color: '#2c3e50', marginBottom: 8, fontWeight: 'bold' },
  divider: { borderBottom: '1px solid #dee2e6', marginVertical: 10 },
  footer:  { position: 'absolute', bottom: 30, left: 30, right: 30, textAlign: 'center', fontSize: 9, color: '#95a5a6' }
});

// ─── Stili PDF Bolla (replica da CreaBollaForm) ───────────────────────────────
const bollaStyles = StyleSheet.create({
  page:             { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  title:            { fontSize: 16, fontWeight: 'bold', textAlign: 'center', marginBottom: 16 },
  section:          { marginBottom: 10 },
  row:              { flexDirection: 'row', marginBottom: 4 },
  label:            { fontWeight: 'bold', width: 160 },
  value:            { flex: 1 },
  tableHeader:      { flexDirection: 'row', backgroundColor: '#e0e0e0', padding: 4, marginBottom: 2, fontWeight: 'bold' },
  tableRow:         { flexDirection: 'row', padding: 4, borderBottom: '1 solid #ccc' },
  col1:             { width: '40%' },
  col2:             { width: '30%' },
  col3:             { width: '30%' },
  divider:          { borderBottom: '1 solid #aaa', marginVertical: 8 },
  firmaImg:         { width: 200, height: 80, border: '1 solid #999' },
  footerRow:        { flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 },
  footerCol:        { width: '45%' },
  footerLabel:      { fontWeight: 'bold', marginBottom: 4 },
  prestazione:      { marginTop: 4, lineHeight: 1.5 },
  intestazione:     { flexDirection: 'row', alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottom: '1 solid #ccc' },
  logoImg:          { width: 80, height: 32, objectFit: 'contain' },
  recapiti:         { marginLeft: 12, fontSize: 9, color: '#333' },
  recapitiNome:     { fontSize: 10, fontWeight: 'bold', marginBottom: 2 },
  numeroBollaBox:   { marginLeft: 'auto', backgroundColor: '#1a1a1a', borderRadius: 4, paddingVertical: 6, paddingHorizontal: 12, alignItems: 'center', justifyContent: 'center' },
  numeroBollaLabel: { fontSize: 7, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 },
  numeroBollaText:  { fontSize: 20, fontWeight: 'bold', color: '#ffffff' },
  page2:            { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  page2Title:       { fontSize: 14, fontWeight: 'bold', textAlign: 'center', marginBottom: 12 },
  page2Subtitle:    { fontSize: 10, color: '#555', textAlign: 'center', marginBottom: 16 },
  fotoPesataImg:    { width: '100%', maxHeight: 620, objectFit: 'contain', border: '1 solid #ccc' },
  fotoPesataBox:    { width: '100%', height: 300, border: '1 solid #ccc', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  fotoPesataNone:   { color: '#999', fontSize: 12 },
});

function fmtData(d) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

// ─── PDF Bolla ────────────────────────────────────────────────────────────────
function BollaPDF({ bolla }) {
  const mezzi = bolla.mezzi || [];
  return (
    <Document>
      <Page size="A4" style={bollaStyles.page}>
        <View style={bollaStyles.intestazione}>
          <Image src="CRG_logo.png" style={bollaStyles.logoImg} />
          <View style={bollaStyles.recapiti}>
            <Text style={bollaStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso (TI)</Text>
            <Text>Tel: +41 78 751 32 51 - email:info@crgscavi.ch</Text>
          </View>
          <View style={bollaStyles.numeroBollaBox}>
            <Text style={bollaStyles.numeroBollaLabel}>N° BOLLA</Text>
            <Text style={bollaStyles.numeroBollaText}>{bolla.numeroBolla}</Text>
          </View>
        </View>

        <Text style={bollaStyles.title}>BOLLA DI LAVORO</Text>

        <View style={bollaStyles.section}>
          <View style={bollaStyles.row}>
            <Text style={bollaStyles.label}>Cliente</Text>
            <Text style={bollaStyles.value}>{bolla.cliente || bolla.clienteId}</Text>
          </View>
          <View style={bollaStyles.row}>
            <Text style={bollaStyles.label}>Cantiere</Text>
            <Text style={bollaStyles.value}>{bolla.cantiere || bolla.cantiereId}</Text>
          </View>
          <View style={bollaStyles.row}>
            <Text style={bollaStyles.label}>Data</Text>
            <Text style={bollaStyles.value}>{fmtData(bolla.data)}</Text>
          </View>
        </View>

        <View style={bollaStyles.divider} />

        <View style={bollaStyles.section}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>Mezzi utilizzati</Text>
          <View style={bollaStyles.tableHeader}>
            <Text style={bollaStyles.col1}>Mezzo</Text>
            <Text style={bollaStyles.col2}>Ore</Text>
            <Text style={bollaStyles.col3}>KM</Text>
          </View>
          {mezzi.filter(m => m.veicoloId).map((m, i) => (
            <View key={i} style={bollaStyles.tableRow}>
              <Text style={bollaStyles.col1}>{m.label || m.veicoloId}</Text>
              <Text style={bollaStyles.col2}>{m.ore || '-'}</Text>
              <Text style={bollaStyles.col3}>{m.km || '-'}</Text>
            </View>
          ))}
        </View>

        <View style={bollaStyles.divider} />

        <View style={bollaStyles.section}>
          <View style={bollaStyles.row}>
            <Text style={bollaStyles.label}>Smaltimento materiale da scavo (m³)</Text>
            <Text style={bollaStyles.value}>{bolla.smaltimentoScavo || '-'}</Text>
          </View>
          <View style={bollaStyles.row}>
            <Text style={bollaStyles.label}>Smaltimento demolizione (m³)</Text>
            <Text style={bollaStyles.value}>{bolla.smaltimentoDemo || '-'}</Text>
          </View>
        </View>

        <View style={bollaStyles.divider} />

        <View style={bollaStyles.section}>
          <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>Prestazione</Text>
          <Text style={bollaStyles.prestazione}>{bolla.prestazione || '-'}</Text>
        </View>

        <View style={bollaStyles.divider} />

        <View style={bollaStyles.footerRow}>
          <View style={bollaStyles.footerCol}>
            <Text style={bollaStyles.footerLabel}>Nome autista</Text>
            <Text>{bolla.nomeAutista}</Text>
          </View>
          <View style={bollaStyles.footerCol}>
            <Text style={bollaStyles.footerLabel}>Firma cliente</Text>
            {bolla.firmaUrl
              ? <Image src={bolla.firmaUrl} style={bollaStyles.firmaImg} />
              : <Text style={{ color: '#999' }}>Non firmato</Text>
            }
            {bolla.nomeClienteFirma ? (
              <Text style={{ marginTop: 4, fontSize: 10 }}>{bolla.nomeClienteFirma}</Text>
            ) : null}
          </View>
        </View>
      </Page>

      <Page size="A4" style={bollaStyles.page2}>
        <Text style={bollaStyles.page2Title}>FOTO PESATA</Text>
        <Text style={bollaStyles.page2Subtitle}>{bolla.cantiere || bolla.cantiereId} — {fmtData(bolla.data)}</Text>
        <View style={bollaStyles.divider} />
        {bolla.fotoPesataUrl
          ? <Image src={bolla.fotoPesataUrl} style={bollaStyles.fotoPesataImg} />
          : (
            <View style={bollaStyles.fotoPesataBox}>
              <Text style={bollaStyles.fotoPesataNone}>Nessuna foto pesata allegata</Text>
            </View>
          )
        }
      </Page>
    </Document>
  );
}

// ─── Bottone Visualizza PDF (apre in nuova tab, NON scarica) ──────────────────
function BottoneVisualizzaPDF({ bolla }) {
  const [loading, setLoading] = useState(false);

  async function apriPDF() {
    setLoading(true);
    try {
      const blob = await pdf(<BollaPDF bolla={bolla} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore generazione PDF: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      onClick={apriPDF}
      disabled={loading}
      title="Visualizza bolla PDF"
      style={{
        background: 'none',
        border: 'none',
        cursor: loading ? 'wait' : 'pointer',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '22px',
        lineHeight: 1,
        transition: 'transform 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
    >
      {loading ? '⏳' : '📄'}
    </button>
  );
}

// ─── PDF Programma Lavorativo ─────────────────────────────────────────────────
function ProgrammaPDF({ programma, dipendente, veicolo, cantiere, cliente }) {
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.header}>PROGRAMMA LAVORATIVO</Text>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>DATA</Text>
          <Text style={pdfStyles.value}>{formattaData(programma.data)}</Text>
        </View>
        <View style={pdfStyles.divider} />
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>DIPENDENTE</Text>
          <Text style={pdfStyles.value}>{dipendente.nome} {dipendente.cognome}</Text>
          <Text style={pdfStyles.label}>ORARIO INIZIO</Text>
          <Text style={pdfStyles.value}>{programma.orarioInizio}</Text>
          <Text style={pdfStyles.label}>ORARIO FINE</Text>
          <Text style={pdfStyles.value}>{programma.orarioFine}</Text>
          <Text style={pdfStyles.label}>DURATA LAVORO</Text>
          <Text style={pdfStyles.value}>{programma.durataLavoro}</Text>
        </View>
        <View style={pdfStyles.divider} />
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>VEICOLO UTILIZZATO</Text>
          <Text style={pdfStyles.value}>{veicolo.marca} {veicolo.modello} - Targa: {veicolo.targa}</Text>
        </View>
        <View style={pdfStyles.divider} />
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.label}>CANTIERE</Text>
          <Text style={pdfStyles.value}>{cantiere.luogo}</Text>
          <Text style={pdfStyles.label}>CLIENTE</Text>
          <Text style={pdfStyles.value}>{cliente.ragioneSociale}</Text>
        </View>
        {programma.note && (
          <>
            <View style={pdfStyles.divider} />
            <View style={pdfStyles.section}>
              <Text style={pdfStyles.label}>NOTE</Text>
              <Text style={pdfStyles.value}>{programma.note}</Text>
            </View>
          </>
        )}
        <Text style={pdfStyles.footer}>
          Documento generato il {new Date().toLocaleDateString('it-IT')} - App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

// ─── PDF Lista Risultati ──────────────────────────────────────────────────────
function ListaRisultatiPDF({ risultati, filtri, dipendentiMap, veicoliMap, cantieriMap, clientiMap }) {
  const getFiltriText = () => {
    const filtriAttivi = [];
    if (filtri.dipendenteId) { const dip = dipendentiMap[filtri.dipendenteId]; filtriAttivi.push(`Dipendente: ${dip?.nome} ${dip?.cognome}`); }
    if (filtri.veicoloId)    { const veic = veicoliMap[filtri.veicoloId]; filtriAttivi.push(`Veicolo: ${veic?.marca} ${veic?.modello} (${veic?.targa})`); }
    if (filtri.clienteId)    { const cli = clientiMap[filtri.clienteId]; filtriAttivi.push(`Cliente: ${cli?.ragioneSociale}`); }
    if (filtri.cantiereId)   { const cant = cantieriMap[filtri.cantiereId]; filtriAttivi.push(`Cantiere: ${cant?.luogo}`); }
    if (filtri.dataInizio)   filtriAttivi.push(`Dal: ${formattaData(filtri.dataInizio)}`);
    if (filtri.dataFine)     filtriAttivi.push(`Al: ${formattaData(filtri.dataFine)}`);
    if (filtri.numeroBolla)  filtriAttivi.push(`Nr. Bolla: ${filtri.numeroBolla}`);
    return filtriAttivi.length > 0 ? filtriAttivi.join(' | ') : 'Nessun filtro applicato';
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={{ padding: 30, fontSize: 10, fontFamily: 'Helvetica' }}>
        <Text style={{ fontSize: 18, marginBottom: 10, textAlign: 'center', color: '#2c3e50', fontWeight: 'bold' }}>
          RISULTATI RICERCA PROGRAMMI
        </Text>
        <View style={{ backgroundColor: '#e3f2fd', padding: 10, marginBottom: 15, borderRadius: 5 }}>
          <Text style={{ fontSize: 11, textAlign: 'center', fontWeight: 'bold' }}>Filtri Applicati</Text>
          <Text style={{ fontSize: 9, textAlign: 'center', marginTop: 5 }}>{getFiltriText()}</Text>
          <Text style={{ fontSize: 9, textAlign: 'center', marginTop: 3 }}>Totale Risultati: {risultati.length}</Text>
        </View>
        <View style={{ display: 'table', width: '100%', marginTop: 10 }}>
          <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#dee2e6', minHeight: 25, alignItems: 'center', backgroundColor: '#f8f9fa', fontWeight: 'bold', fontSize: 9 }}>
            <Text style={{ padding: 5, width: '10%' }}>Data</Text>
            <Text style={{ padding: 5, width: '15%' }}>Dipendente</Text>
            <Text style={{ padding: 5, width: '8%' }}>Inizio</Text>
            <Text style={{ padding: 5, width: '8%' }}>Fine</Text>
            <Text style={{ padding: 5, width: '8%' }}>Durata</Text>
            <Text style={{ padding: 5, width: '13%' }}>Veicolo</Text>
            <Text style={{ padding: 5, width: '15%' }}>Cliente</Text>
            <Text style={{ padding: 5, width: '13%' }}>Cantiere</Text>
            <Text style={{ padding: 5, width: '10%' }}>Note</Text>
          </View>
          {risultati.map((prog, index) => {
            const dipendente = dipendentiMap[prog.dipendenteId];
            const veicolo    = veicoliMap[prog.veicoloId];
            const cantiere   = cantieriMap[prog.cantiereId];
            const cliente    = cantiere ? clientiMap[cantiere.clienteId] : null;
            return (
              <View key={index} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#dee2e6', minHeight: 25, alignItems: 'center' }}>
                <Text style={{ padding: 5, fontSize: 8, width: '10%' }}>{formattaData(prog.data)}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '15%' }}>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.orarioInizio}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.orarioFine}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '8%' }}>{prog.durataLavoro}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '13%' }}>{veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '15%' }}>{cliente ? cliente.ragioneSociale : 'N/D'}</Text>
                <Text style={{ padding: 5, fontSize: 8, width: '13%' }}>{cantiere ? cantiere.luogo : 'N/D'}</Text>
                <Text style={{ padding: 5, fontSize: 7, width: '10%' }}>{prog.note ? prog.note.substring(0, 30) + (prog.note.length > 30 ? '...' : '') : '-'}</Text>
              </View>
            );
          })}
        </View>
        <Text style={{ position: 'absolute', bottom: 30, left: 30, right: 30, textAlign: 'center', fontSize: 8, color: '#95a5a6' }}>
          Report generato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT')} - App Cantieri
        </Text>
      </Page>
    </Document>
  );
}

// ─── Componente Principale ────────────────────────────────────────────────────
function RicercaProgrammi() {
  const [programmi, setProgrammi]           = useState([]);
  const [dipendenti, setDipendenti]         = useState([]);
  const [veicoli, setVeicoli]               = useState([]);
  const [cantieri, setCantieri]             = useState([]);
  const [clienti, setClienti]               = useState([]);
  const [risultati, setRisultati]           = useState([]);
  const [ricercaEffettuata, setRicercaEffettuata] = useState(false);

  const [filtri, setFiltri] = useState({
    dipendenteId: '',
    veicoloId:    '',
    clienteId:    '',
    cantiereId:   '',
    dataInizio:   '',
    dataFine:     '',
    numeroBolla:  ''
  });

  useEffect(() => { caricaTuttiDati(); }, []);

  async function caricaTuttiDati() {
    await Promise.all([
      caricaDipendenti(),
      caricaVeicoli(),
      caricaCantieri(),
      caricaClienti(),
      caricaProgrammi()
    ]);
  }

  async function caricaDipendenti() {
    const snapshot = await get(ref(database, 'dipendenti'));
    if (snapshot.exists()) {
      const data = snapshot.val();
      setDipendenti(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaVeicoli() {
    const snapshot = await get(ref(database, 'veicoli'));
    if (snapshot.exists()) {
      const data = snapshot.val();
      setVeicoli(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaCantieri() {
    const snapshot = await get(ref(database, 'cantieri'));
    if (snapshot.exists()) {
      const data = snapshot.val();
      setCantieri(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaClienti() {
    const snapshot = await get(ref(database, 'clienti'));
    if (snapshot.exists()) {
      const data = snapshot.val();
      setClienti(Object.keys(data).map(key => ({ id: key, ...data[key] })));
    }
  }

  async function caricaProgrammi() {
    const [progSnap, bolleSnap] = await Promise.all([
      get(ref(database, 'programmi-lavorativi')),
      get(ref(database, 'bolleForm')),
    ]);
    const programmiArray = progSnap.exists()
      ? Object.keys(progSnap.val()).map(key => ({ id: key, ...progSnap.val()[key] }))
      : [];
    const bolleArray = bolleSnap.exists()
      ? Object.keys(bolleSnap.val()).map(key => ({ id: key, _isBolla: true, ...bolleSnap.val()[key] }))
      : [];
    setProgrammi([...programmiArray, ...bolleArray]);
  }

  function handleRicerca(e) {
    e.preventDefault();
    let programmiFiltrati = [...programmi];

    if (filtri.dipendenteId) {
      programmiFiltrati = programmiFiltrati.filter(p => p.dipendenteId === filtri.dipendenteId);
    }
    if (filtri.veicoloId) {
      programmiFiltrati = programmiFiltrati.filter(p =>
        p.veicoloId === filtri.veicoloId ||
        (Array.isArray(p.mezzi) && p.mezzi.some(m => m.veicoloId === filtri.veicoloId))
      );
    }
    if (filtri.cantiereId) {
      programmiFiltrati = programmiFiltrati.filter(p => p.cantiereId === filtri.cantiereId);
    }
    if (filtri.clienteId) {
      const cantieriDelCliente = cantieri.filter(c => c.clienteId === filtri.clienteId).map(c => c.id);
      programmiFiltrati = programmiFiltrati.filter(
        p => cantieriDelCliente.includes(p.cantiereId) || p.clienteId === filtri.clienteId
      );
    }
    if (filtri.dataInizio) {
      programmiFiltrati = programmiFiltrati.filter(p => p.data >= filtri.dataInizio);
    }
    if (filtri.dataFine) {
      programmiFiltrati = programmiFiltrati.filter(p => p.data <= filtri.dataFine);
    }
    if (filtri.numeroBolla) {
      programmiFiltrati = programmiFiltrati.filter(p =>
        String(p.numeroBolla ?? '').toLowerCase().includes(filtri.numeroBolla.toLowerCase())
      );
    }

    programmiFiltrati.sort((a, b) => new Date(b.data) - new Date(a.data));
    setRisultati(programmiFiltrati);
    setRicercaEffettuata(true);
  }

  function resetFiltri() {
    setFiltri({ dipendenteId: '', veicoloId: '', clienteId: '', cantiereId: '', dataInizio: '', dataFine: '', numeroBolla: '' });
    setRisultati([]);
    setRicercaEffettuata(false);
  }

  function getDipendente(id)   { return dipendenti.find(d => d.id === id); }
  function getVeicolo(id)      { return veicoli.find(v => v.id === id); }
  function getCantiere(id)     { return cantieri.find(c => c.id === id); }
  function getCliente(cantiereId) {
    const cantiere = getCantiere(cantiereId);
    return cantiere ? clienti.find(c => c.id === cantiere.clienteId) : null;
  }

  function arricchisciBolla(bolla) {
    const cantiere = getCantiere(bolla.cantiereId);
    const cliente  = cantiere
      ? clienti.find(c => c.id === cantiere.clienteId)
      : clienti.find(c => c.id === bolla.clienteId);
    return {
      ...bolla,
      cantiere: cantiere?.luogo || bolla.cantiereId,
      cliente:  cliente?.ragioneSociale || bolla.clienteId,
    };
  }

  function getDescrizioneFiltri() {
    const filtriAttivi = [];
    if (filtri.dipendenteId) { const dip = dipendenti.find(d => d.id === filtri.dipendenteId); filtriAttivi.push(`Dipendente: ${dip?.nome} ${dip?.cognome}`); }
    if (filtri.veicoloId)    { const veic = veicoli.find(v => v.id === filtri.veicoloId); filtriAttivi.push(`Veicolo: ${veic?.marca} ${veic?.modello}`); }
    if (filtri.clienteId)    { const cli = clienti.find(c => c.id === filtri.clienteId); filtriAttivi.push(`Cliente: ${cli?.ragioneSociale}`); }
    if (filtri.cantiereId)   { const cant = cantieri.find(c => c.id === filtri.cantiereId); filtriAttivi.push(`Cantiere: ${cant?.luogo}`); }
    if (filtri.dataInizio)   filtriAttivi.push(`Dal: ${formattaData(filtri.dataInizio)}`);
    if (filtri.dataFine)     filtriAttivi.push(`Al: ${formattaData(filtri.dataFine)}`);
    if (filtri.numeroBolla)  filtriAttivi.push(`Nr. Bolla: ${filtri.numeroBolla}`);
    return filtriAttivi.join(' | ');
  }

  return (
    <div className="lista-container">
      <div className="lista-header">
        <h2>🔍 Ricerca Programmi</h2>
      </div>

      {/* ── FILTRI ── */}
      <div style={{background: '#f8f9fa', padding: '25px', borderRadius: '10px', marginBottom: '30px'}}>
        <form onSubmit={handleRicerca}>
          <div className="form-ricerca-programmi">

            <div className="form-group" style={{marginBottom: 0}}>
              <label>👤 Dipendente</label>
              <select value={filtri.dipendenteId} onChange={e => setFiltri({...filtri, dipendenteId: e.target.value})}>
                <option value="">-- Tutti --</option>
                {dipendenti.map(dip => <option key={dip.id} value={dip.id}>{dip.nome} {dip.cognome}</option>)}
              </select>
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>🚚 Veicolo</label>
              <select value={filtri.veicoloId} onChange={e => setFiltri({...filtri, veicoloId: e.target.value})}>
                <option value="">-- Tutti --</option>
                {veicoli.map(veic => <option key={veic.id} value={veic.id}>{veic.marca} {veic.modello} ({veic.targa})</option>)}
              </select>
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>🏢 Cliente</label>
              <select value={filtri.clienteId} onChange={e => setFiltri({...filtri, clienteId: e.target.value})}>
                <option value="">-- Tutti --</option>
                {clienti.map(cli => <option key={cli.id} value={cli.id}>{cli.ragioneSociale}</option>)}
              </select>
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>🏗️ Cantiere</label>
              <select value={filtri.cantiereId} onChange={e => setFiltri({...filtri, cantiereId: e.target.value})}>
                <option value="">-- Tutti --</option>
                {cantieri.map(cant => <option key={cant.id} value={cant.id}>{cant.luogo}</option>)}
              </select>
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>📅 Data Inizio</label>
              <input type="date" value={filtri.dataInizio} onChange={e => setFiltri({...filtri, dataInizio: e.target.value})} />
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>📅 Data Fine</label>
              <input type="date" value={filtri.dataFine} onChange={e => setFiltri({...filtri, dataFine: e.target.value})} />
            </div>

            <div className="form-group" style={{marginBottom: 0}}>
              <label>🧾 Nr. Bolla</label>
              <input
                type="text"
                placeholder="Es. 42"
                value={filtri.numeroBolla}
                onChange={e => setFiltri({...filtri, numeroBolla: e.target.value})}
              />
            </div>

          </div>

          <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
            <button type="submit" className="btn-add" style={{flex: 1}}>🔍 Cerca</button>
            <button type="button" className="btn-cancel" onClick={resetFiltri} style={{flex: 1}}>🔄 Reset</button>
          </div>
        </form>
      </div>

      {/* ── STATI VUOTI ── */}
      {!ricercaEffettuata ? (
        <div className="empty-state">
          <p>👆 Usa i filtri sopra per cercare i programmi lavorativi.</p>
          <p>Puoi combinare più filtri per affinare la ricerca.</p>
        </div>
      ) : risultati.length === 0 ? (
        <div className="empty-state">
          <p>❌ Nessun risultato trovato con i filtri selezionati.</p>
          <p>Prova a modificare i criteri di ricerca.</p>
        </div>
      ) : (
        <>
          {/* ── BANNER RISULTATI ── */}
          <div style={{
            background: '#efd400',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            border: 'solid 1px #454545'
          }}>
            <div>
              <strong style={{ fontSize: '16px' }}>✅ Trovati {risultati.length} programmi</strong>
              {getDescrizioneFiltri() && (
                <p style={{margin: '5px 0 0 0', fontSize: '13px', color: '#333'}}>{getDescrizioneFiltri()}</p>
              )}
            </div>
            <PulsantiCondivisionePDF
              documentoPDF={
                <ListaRisultatiPDF
                  risultati={risultati}
                  filtri={filtri}
                  dipendentiMap={dipendenti.reduce((acc, d) => ({ ...acc, [d.id]: d }), {})}
                  veicoliMap={veicoli.reduce((acc, v) => ({ ...acc, [v.id]: v }), {})}
                  cantieriMap={cantieri.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})}
                  clientiMap={clienti.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})}
                />
              }
              nomeFile={`ricerca_programmi_${filtri.dataInizio || 'tutte'}_${filtri.dataFine || 'tutte'}.pdf`}
              testoCondivisione={`🔍 Risultati Ricerca Programmi\n\n${risultati.length} programmi trovati\n${getDescrizioneFiltri() || 'Tutti i programmi'}`}
              emailDestinatario=""
              oggettoEmail={`Ricerca Programmi - ${risultati.length} risultati`}
              corpoEmail={`Buongiorno,\n\nallego i risultati della ricerca programmi.\n\nFILTRI APPLICATI:\n${getDescrizioneFiltri() || 'Nessun filtro'}\n\nRISULTATI: ${risultati.length} programmi trovati\n\nCordiali saluti`}
            />
          </div>

          {/* ── TABELLA DESKTOP ── */}
          <div className="desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Dipendente</th>
                  <th>Cantiere</th>
                  <th>Cliente</th>
                  <th style={{textAlign: 'center'}}>Firmata</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {risultati.map(prog => {
                  const dipendente = getDipendente(prog.dipendenteId);
                  const cantiere   = getCantiere(prog.cantiereId);
                  const cliente    = getCliente(prog.cantiereId) || clienti.find(c => c.id === prog.clienteId);

                  // ── Riga BOLLA ──
                  if (prog._isBolla) {
                    return (
                      <tr key={prog.id} style={{ background: '#fffbea' }}>
                        <td><strong>{formattaData(prog.data)}</strong></td>
                        <td>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : prog.nomeAutista || 'N/D'}</td>
                        <td>{cantiere ? cantiere.luogo : 'N/D'}</td>
                        <td>{cliente ? cliente.ragioneSociale : 'N/D'}</td>
                        <td style={{ textAlign: 'center', fontSize: '18px' }}>
                          {prog.firmaUrl ? '✅' : '❌'}
                        </td>
                        <td className="actions">
                          <BottoneVisualizzaPDF bolla={arricchisciBolla(prog)} />
                        </td>
                      </tr>
                    );
                  }

                  // ── Riga PROGRAMMA LAVORATIVO ──
                  const veicolo = getVeicolo(prog.veicoloId);
                  return (
                    <tr key={prog.id}>
                      <td><strong>{formattaData(prog.data)}</strong></td>
                      <td>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}</td>
                      <td>{cantiere ? cantiere.luogo : 'N/D'}</td>
                      <td>{cliente ? cliente.ragioneSociale : 'N/D'}</td>
                      <td style={{ textAlign: 'center', fontSize: '18px' }}>—</td>
                      <td className="actions">
                        {dipendente && veicolo && cantiere && cliente && (
                          <PulsantiCondivisionePDF
                            documentoPDF={
                              <ProgrammaPDF
                                programma={prog}
                                dipendente={dipendente}
                                veicolo={veicolo}
                                cantiere={cantiere}
                                cliente={cliente}
                              />
                            }
                            nomeFile={`ricerca_${prog.data}_${dipendente.cognome}.pdf`}
                            testoCondivisione={`📋 Programma Lavorativo ${formattaData(prog.data)}\n\n👤 ${dipendente.nome} ${dipendente.cognome}\n🏗️ ${cantiere.luogo}\n🏢 ${cliente.ragioneSociale}\n⏰ ${prog.orarioInizio} - ${prog.orarioFine}`}
                            emailDestinatario=""
                            oggettoEmail={`Programma Lavorativo - ${formattaData(prog.data)}`}
                            corpoEmail={`Buongiorno,\n\nallego il programma lavorativo del ${formattaData(prog.data)}.\n\nDIPENDENTE: ${dipendente.nome} ${dipendente.cognome}\nCANTIERE: ${cantiere.luogo}\nCLIENTE: ${cliente.ragioneSociale}\nVEICOLO: ${veicolo.marca} ${veicolo.modello} (${veicolo.targa})\nORARIO: ${prog.orarioInizio} - ${prog.orarioFine}\n\nCordiali saluti`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── CARD MOBILE ── */}
          <div className="mobile-cards">
            {risultati.map(prog => {
              const dipendente = getDipendente(prog.dipendenteId);
              const cantiere   = getCantiere(prog.cantiereId);
              const cliente    = getCliente(prog.cantiereId) || clienti.find(c => c.id === prog.clienteId);

              // ── Card BOLLA ──
              if (prog._isBolla) {
                return (
                  <div key={prog.id} className="mobile-card" style={{ borderLeft: '4px solid #efd400' }}>
                    <div className="mobile-card-header">
                      📅 {formattaData(prog.data)} &nbsp;·&nbsp; 🧾 Bolla n° <strong>{prog.numeroBolla}</strong>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">👤 Dipendente:</span>
                      <span className="mobile-card-value">
                        {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : prog.nomeAutista || 'N/D'}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">🏢 Cliente:</span>
                      <span className="mobile-card-value">{cliente ? cliente.ragioneSociale : 'N/D'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">🏗️ Cantiere:</span>
                      <span className="mobile-card-value">{cantiere ? cantiere.luogo : 'N/D'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">✍️ Firmata:</span>
                      <span className="mobile-card-value" style={{ fontSize: '18px' }}>
                        {prog.firmaUrl ? '✅' : '❌'}
                      </span>
                    </div>
                    <div className="mobile-card-actions">
                      <BottoneVisualizzaPDF bolla={arricchisciBolla(prog)} />
                    </div>
                  </div>
                );
              }

              // ── Card PROGRAMMA LAVORATIVO ──
              const veicolo = getVeicolo(prog.veicoloId);
              return (
                <div key={prog.id} className="mobile-card">
                  <div className="mobile-card-header">
                    📅 {formattaData(prog.data)}
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">👤 Dipendente:</span>
                    <span className="mobile-card-value">
                      {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🕐 Orari:</span>
                    <span className="mobile-card-value">
                      {prog.orarioInizio} - {prog.orarioFine} ({prog.durataLavoro})
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🚚 Veicolo:</span>
                    <span className="mobile-card-value">
                      {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏗️ Cantiere:</span>
                    <span className="mobile-card-value">{cantiere ? cantiere.luogo : 'N/D'}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">🏢 Cliente:</span>
                    <span className="mobile-card-value">{cliente ? cliente.ragioneSociale : 'N/D'}</span>
                  </div>
                  <div className="mobile-card-actions">
                    {dipendente && veicolo && cantiere && cliente && (
                      <PulsantiCondivisionePDF
                        documentoPDF={
                          <ProgrammaPDF
                            programma={prog}
                            dipendente={dipendente}
                            veicolo={veicolo}
                            cantiere={cantiere}
                            cliente={cliente}
                          />
                        }
                        nomeFile={`ricerca_${prog.data}_${dipendente.cognome}.pdf`}
                        testoCondivisione={`📋 Programma Lavorativo ${formattaData(prog.data)}\n\n👤 ${dipendente.nome} ${dipendente.cognome}\n🏗️ ${cantiere.luogo}\n🏢 ${cliente.ragioneSociale}\n⏰ ${prog.orarioInizio} - ${prog.orarioFine}`}
                        emailDestinatario=""
                        oggettoEmail={`Programma Lavorativo - ${formattaData(prog.data)}`}
                        corpoEmail={`Buongiorno,\n\nallego il programma lavorativo del ${formattaData(prog.data)}.\n\nDIPENDENTE: ${dipendente.nome} ${dipendente.cognome}\nCANTIERE: ${cantiere.luogo}\nCLIENTE: ${cliente.ragioneSociale}\nVEICOLO: ${veicolo.marca} ${veicolo.modello} (${veicolo.targa})\nORARIO: ${prog.orarioInizio} - ${prog.orarioFine}\n\nCordiali saluti`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default RicercaProgrammi;
